import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import PageBreadcrumbs from "../components/PageBreadcrumbs";
import { useShows } from "../hooks/useShows";

function ShowListPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { shows, loading, error } = useShows();

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MM/dd/yyyy");
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "success";
      case "CLOSED":
        return "error";
      case "PREVIEW":
        return "warning";
      default:
        return "default";
    }
  };

  const handleRowClick = (showId) => {
    navigate(`/shows/${showId}`);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageBreadcrumbs items={[{ label: t("shows.list.title") }]} />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("shows.list.columns.showId")}</TableCell>
              <TableCell>{t("shows.list.columns.name")}</TableCell>
              <TableCell>{t("shows.list.columns.status")}</TableCell>
              <TableCell>{t("shows.list.columns.showDates")}</TableCell>
              <TableCell>{t("shows.list.columns.entriesClose")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shows.map((show) => (
              <TableRow
                key={show.id}
                onClick={() => handleRowClick(show.id)}
                hover
                sx={{
                  cursor: "pointer",
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <TableCell>{show.fvdShowID || "-"}</TableCell>
                <TableCell>{show.name}</TableCell>
                <TableCell>
                  <Chip
                    label={show.status}
                    color={getStatusColor(show.status)}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                  />
                </TableCell>
                <TableCell>
                  {show.showTime?.open && show.showTime?.close
                    ? `${formatDate(show.showTime.open)} - ${formatDate(
                      show.showTime.close
                    )}`
                    : "-"}
                </TableCell>
                <TableCell>
                  {show.entryTime?.close
                    ? formatDate(show.entryTime.close)
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
            {shows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t("shows.list.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ShowListPage;
